import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@state/store';
import { updateWind } from '@state/slices/studySlice';
import { DEFAULT_WIND_CONVENTION } from '@contracts/index';

/**
 * WindInspector (Gate 1 Foundation)
 *
 * Exposes user-defined incoming wind parameters:
 * - Speed (numeric, m/s, strictly ≥ 0, UNSET by default)
 * - Direction (numeric angular input, UNSET by default, local Cartesian convention)
 * - Visual 2D directional indicator widget (visual only, NOT CFD flow)
 *
 * G1 RULE:
 * Zero implicit default values (no hidden 5.0 m/s or 0°).
 * Speed and direction must be explicitly supplied by the user.
 */
export const WindInspector: React.FC = () => {
  const dispatch = useAppDispatch();
  const activeScenarioId = useAppSelector((state) => state.study.study.activeScenarioId);
  const activeScenario = useAppSelector(
    (state) => state.study.study.scenarios[activeScenarioId]
  );
  const wind = activeScenario?.cfdSetup?.wind;

  const speed = wind?.speed ?? null;
  const direction = wind?.direction ?? null;

  // Local text inputs to permit clean user typing and clearing
  const [speedInput, setSpeedInput] = useState<string>(
    speed !== null ? speed.toString() : ''
  );
  const [directionInput, setDirectionInput] = useState<string>(
    direction !== null ? direction.toString() : ''
  );

  // Sync local inputs when scenario changes or external update occurs
  useEffect(() => {
    setSpeedInput(speed !== null ? speed.toString() : '');
  }, [speed, activeScenarioId]);

  useEffect(() => {
    setDirectionInput(direction !== null ? direction.toString() : '');
  }, [direction, activeScenarioId]);

  const handleSpeedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setSpeedInput(raw);
    if (raw.trim() === '') {
      dispatch(updateWind({ speed: null }));
      return;
    }
    const val = parseFloat(raw);
    if (!isNaN(val)) {
      dispatch(updateWind({ speed: Math.max(0, val) }));
    }
  };

  const handleDirectionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setDirectionInput(raw);
    if (raw.trim() === '') {
      dispatch(updateWind({ direction: null }));
      return;
    }
    const val = parseFloat(raw);
    if (!isNaN(val)) {
      const normalized = ((val % 360) + 360) % 360;
      dispatch(updateWind({ direction: normalized }));
    }
  };

  const handleApplyPreset = (deg: number) => {
    setDirectionInput(deg.toString());
    dispatch(updateWind({ direction: deg }));
  };

  // Convert angular direction to unit vector if defined (0° = +X, 90° = +Z)
  const isDirectionSet = direction !== null && Number.isFinite(direction);
  const rad = isDirectionSet ? (direction * Math.PI) / 180 : 0;
  const dirX = isDirectionSet ? Math.cos(rad) : null;
  const dirZ = isDirectionSet ? Math.sin(rad) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', fontSize: '11.5px' }}>
      {/* Header Info */}
      <div style={{ borderBottom: '1px solid rgba(47, 120, 196, 0.12)', paddingBottom: '8px' }}>
        <div
          style={{
            fontFamily: 'ui-monospace, monospace',
            fontSize: '9px',
            fontWeight: 700,
            color: '#2F78C4',
            letterSpacing: '0.12em',
            marginBottom: '2px'
          }}
        >
          PRESCRIBED BOUNDARY CONDITION
        </div>
        <div style={{ fontWeight: 600, fontSize: '13px', color: '#0f172a' }}>
          Wind Parameters
        </div>
        <div style={{ fontSize: '10.5px', color: '#64748b', marginTop: '2px' }}>
          Scenario: <strong>{activeScenario?.name || 'Baseline'}</strong>
        </div>
      </div>

      {/* Wind Speed Input */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <label
          htmlFor="wind-speed-input"
          style={{ fontWeight: 600, color: '#334155', display: 'flex', justifyContent: 'space-between' }}
        >
          <span>Wind Speed</span>
          {speed !== null ? (
            <span style={{ fontFamily: 'ui-monospace, monospace', color: '#2450A6', fontWeight: 700 }}>
              {speed.toFixed(1)} m/s
            </span>
          ) : (
            <span style={{ fontFamily: 'ui-monospace, monospace', color: '#d97706', fontWeight: 600, fontSize: '10px' }}>
              Required
            </span>
          )}
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <input
            id="wind-speed-input"
            type="number"
            min="0"
            step="0.5"
            placeholder="e.g. 5.0"
            value={speedInput}
            onChange={handleSpeedChange}
            style={{
              flex: 1,
              height: '28px',
              padding: '0 8px',
              borderRadius: '3px',
              border: speed === null ? '1px solid rgba(217, 119, 6, 0.45)' : '1px solid rgba(47, 120, 196, 0.25)',
              background: '#ffffff',
              fontSize: '12px',
              fontFamily: 'ui-monospace, monospace',
              color: '#0f172a'
            }}
          />
          <span
            style={{
              fontFamily: 'ui-monospace, monospace',
              fontSize: '10px',
              padding: '4px 6px',
              background: 'rgba(47, 120, 196, 0.08)',
              borderRadius: '3px',
              color: '#475569',
              fontWeight: 600
            }}
          >
            m/s
          </span>
        </div>
        <span style={{ fontSize: '9.5px', color: speed === null ? '#b45309' : '#64748b' }}>
          {speed === null
            ? '⚠ User input required. Numeric value in SI units (m/s).'
            : 'Numeric value in SI units (m/s). Values below 0 are rejected.'}
        </span>
      </div>

      {/* Wind Direction Input */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <label
          htmlFor="wind-direction-input"
          style={{ fontWeight: 600, color: '#334155', display: 'flex', justifyContent: 'space-between' }}
        >
          <span>Wind Direction</span>
          {direction !== null ? (
            <span style={{ fontFamily: 'ui-monospace, monospace', color: '#2450A6', fontWeight: 700 }}>
              {direction.toFixed(0)}°
            </span>
          ) : (
            <span style={{ fontFamily: 'ui-monospace, monospace', color: '#d97706', fontWeight: 600, fontSize: '10px' }}>
              Required
            </span>
          )}
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <input
            id="wind-direction-input"
            type="number"
            min="0"
            max="360"
            step="5"
            placeholder="e.g. 0 to 360"
            value={directionInput}
            onChange={handleDirectionChange}
            style={{
              flex: 1,
              height: '28px',
              padding: '0 8px',
              borderRadius: '3px',
              border: direction === null ? '1px solid rgba(217, 119, 6, 0.45)' : '1px solid rgba(47, 120, 196, 0.25)',
              background: '#ffffff',
              fontSize: '12px',
              fontFamily: 'ui-monospace, monospace',
              color: '#0f172a'
            }}
          />
          <span
            style={{
              fontFamily: 'ui-monospace, monospace',
              fontSize: '10px',
              padding: '4px 8px',
              background: 'rgba(47, 120, 196, 0.08)',
              borderRadius: '3px',
              color: '#475569',
              fontWeight: 600
            }}
          >
            deg (°)
          </span>
        </div>

        {/* Angular Slider (active only when user has entered a direction or uses it to set one) */}
        <input
          type="range"
          min="0"
          max="360"
          step="1"
          value={direction !== null ? direction : 0}
          onChange={handleDirectionChange}
          style={{
            width: '100%',
            cursor: 'pointer',
            accentColor: '#2450A6',
            marginTop: '2px',
            opacity: direction === null ? 0.6 : 1
          }}
          aria-label="Wind direction slider"
        />

        {/* Direction Presets (explicit user shortcuts, not defaults) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap', marginTop: '4px' }}>
          <span style={{ fontSize: '9px', color: '#64748b' }}>Presets:</span>
          {[
            { label: '0° (+X)', val: 0 },
            { label: '45°', val: 45 },
            { label: '90° (+Z)', val: 90 },
            { label: '180° (-X)', val: 180 },
            { label: '270° (-Z)', val: 270 }
          ].map((preset) => (
            <button
              key={preset.val}
              type="button"
              onClick={() => handleApplyPreset(preset.val)}
              style={{
                fontSize: '9px',
                fontFamily: 'ui-monospace, monospace',
                padding: '2px 5px',
                borderRadius: '2px',
                border: '1px solid rgba(47, 120, 196, 0.18)',
                background: direction === preset.val ? '#2450A6' : 'rgba(248, 246, 241, 0.8)',
                color: direction === preset.val ? '#ffffff' : '#475569',
                cursor: 'pointer'
              }}
            >
              {preset.label}
            </button>
          ))}
          {direction !== null && (
            <button
              type="button"
              onClick={() => {
                setDirectionInput('');
                dispatch(updateWind({ direction: null }));
              }}
              style={{
                fontSize: '9px',
                padding: '2px 5px',
                borderRadius: '2px',
                border: '1px solid rgba(217, 119, 6, 0.3)',
                background: 'transparent',
                color: '#b45309',
                cursor: 'pointer'
              }}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Visual Direction Indicator (2D Dial) */}
      <div
        style={{
          border: '1px solid rgba(47, 120, 196, 0.18)',
          borderRadius: '4px',
          background: 'rgba(248, 246, 241, 0.85)',
          padding: '10px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '8px'
        }}
      >
        <div
          style={{
            fontFamily: 'ui-monospace, monospace',
            fontSize: '9px',
            fontWeight: 700,
            letterSpacing: '0.08em',
            color: '#475569'
          }}
        >
          VISUAL DIRECTION INDICATOR
        </div>

        {/* SVG Compass Dial */}
        <svg
          viewBox="-50 -50 100 100"
          width="110"
          height="110"
          style={{ overflow: 'visible' }}
          aria-label="Visual Wind Direction Indicator"
        >
          {/* Dial Background Ring */}
          <circle cx="0" cy="0" r="42" fill="#FAF8F5" stroke="rgba(47, 120, 196, 0.22)" strokeWidth="1.5" />
          <circle cx="0" cy="0" r="2" fill="#2450A6" />

          {/* Coordinate axes */}
          <line x1="-38" y1="0" x2="38" y2="0" stroke="rgba(47, 120, 196, 0.15)" strokeWidth="1" strokeDasharray="2,2" />
          <line x1="0" y1="-38" x2="0" y2="38" stroke="rgba(47, 120, 196, 0.15)" strokeWidth="1" strokeDasharray="2,2" />

          {/* Coordinate Labels: Strictly local Cartesian reference, zero geographic terms */}
          <text x="44" y="3" fontSize="7" fill="#D94A32" fontWeight="700" textAnchor="start" fontFamily="ui-monospace">+X (0°)</text>
          <text x="0" y="47" fontSize="7" fill="#2F78C4" fontWeight="700" textAnchor="middle" fontFamily="ui-monospace">+Z (90°)</text>
          <text x="-44" y="3" fontSize="7" fill="#64748b" fontWeight="600" textAnchor="end" fontFamily="ui-monospace">-X (180°)</text>
          <text x="0" y="-42" fontSize="7" fill="#64748b" fontWeight="600" textAnchor="middle" fontFamily="ui-monospace">-Z (270°)</text>

          {/* Direction indicator representation */}
          {isDirectionSet ? (
            <g transform={`rotate(${direction})`}>
              {/* Arrow Shaft */}
              <line x1="-22" y1="0" x2="28" y2="0" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round" />
              {/* Arrowhead */}
              <polygon points="34,0 24,-5 24,5" fill="#f97316" />
              <circle cx="-22" cy="0" r="2.5" fill="#f97316" />
            </g>
          ) : (
            <g>
              {/* Neutral Unset Indicator */}
              <circle cx="0" cy="0" r="16" fill="none" stroke="#d97706" strokeWidth="1" strokeDasharray="3,3" />
              <text x="0" y="3" fontSize="6.5" fill="#d97706" fontWeight="600" textAnchor="middle" fontFamily="ui-monospace">
                UNSET
              </text>
            </g>
          )}
        </svg>

        {/* Vector readout */}
        <div
          style={{
            fontFamily: 'ui-monospace, monospace',
            fontSize: '9.5px',
            color: isDirectionSet ? '#334155' : '#b45309',
            background: isDirectionSet ? 'rgba(47, 120, 196, 0.08)' : 'rgba(234, 179, 8, 0.1)',
            padding: '2px 8px',
            borderRadius: '2px'
          }}
        >
          {isDirectionSet
            ? `Vector: [${dirX?.toFixed(3)}, 0, ${dirZ?.toFixed(3)}]`
            : 'Vector: [unset, 0, unset]'}
        </div>

        {/* Scientific Rule Disclaimer */}
        <div
          style={{
            fontSize: '8.5px',
            color: '#64748b',
            textAlign: 'center',
            lineHeight: 1.3,
            maxWidth: '220px'
          }}
        >
          Prescribed incoming wind condition indicator — not CFD flow.
        </div>
      </div>

      {/* Coordinate Convention Documentation */}
      <div
        style={{
          borderLeft: '3px solid #2450A6',
          padding: '6px 10px',
          background: 'rgba(36, 80, 166, 0.05)',
          borderRadius: '0 3px 3px 0',
          fontSize: '10px',
          color: '#334155',
          lineHeight: 1.4
        }}
      >
        <div style={{ fontWeight: 600, color: '#1e3a8a', marginBottom: '2px' }}>
          Coordinate Reference Convention
        </div>
        <div>
          {DEFAULT_WIND_CONVENTION}
        </div>
      </div>
    </div>
  );
};

export default WindInspector;
